import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { fetchTpeHistory, type TpeHistoryRun } from "../api/client";

/** Read-only run history. Any authenticated user can view. */

function fmtDateTime(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDurationSec(secs: number | null) {
  if (secs === null || secs === undefined) return "—";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function fmtUsd(v: number, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `$${Number(v).toFixed(digits)}`;
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, [string, string]> = {
    RUNNING: ["#1e40af", "#93c5fd"],
    SUCCEEDED: ["#14532d", "#86efac"],
    FAILED: ["#7f1d1d", "#fca5a5"],
    TIMED_OUT: ["#7c2d12", "#fdba74"],
    ABORTED: ["#4c1d95", "#c4b5fd"],
  };
  const [bg, fg] = colors[status.toUpperCase()] ?? ["#374151", "#d1d5db"];
  return (
    <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
}

function TickerList({ tickers, isSubset }: { tickers: string[]; isSubset?: boolean }) {
  if (tickers.length === 0) return <span style={{ color: "#94a3b8" }}>—</span>;
  const preview = tickers.slice(0, 4).join(", ");
  const more = tickers.length - 4;
  // Subset runs are kicked off via "Run P1 for ticker subset…" — surface
  // the badge inline so a user scanning the history table can tell at a
  // glance which rows ran the whole portfolio vs a hand-picked subset.
  return (
    <span title={tickers.join(", ") + (isSubset ? " (ticker subset)" : "")} style={{ fontSize: 12 }}>
      {isSubset && (
        <span
          title="Phase 1 was scoped to a manually-picked subset of tickers"
          style={{
            background: "#7c2d12", color: "#fed7aa",
            border: "1px solid #9a3412",
            padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 700,
            marginRight: 6, letterSpacing: 0.3,
          }}
        >SUBSET</span>
      )}
      {preview}{more > 0 ? ` +${more}` : ""}
    </span>
  );
}

export function TpeHistoryPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tpe-history"],
    queryFn: fetchTpeHistory,
    staleTime: 60_000,
  });

  function toggle(runId: string) {
    setSelected((cur) =>
      cur.includes(runId) ? cur.filter((x) => x !== runId) : [...cur, runId].slice(-15),
    );
  }

  const compareDisabled = selected.length < 2;
  const compareHref = useMemo(
    () => `/runs/compare?ids=${selected.map((id) => encodeURIComponent(id)).join(",")}`,
    [selected],
  );

  return (
    <div>
      <Link to="/" className="backlink">← Leaderboard</Link>
      <h2 style={{ marginTop: 12 }}>TPE run history</h2>
      <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 0 }}>
        All historical TPE backtest runs. Cost is estimated using the current
        spot rate × instance-hours from ASG scaling activities (ASG retains
        those ~6 weeks, so older runs may under-report). "Spot kills" counts
        involuntary mid-run instance terminations.
      </p>

      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          style={{
            background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, cursor: "pointer",
          }}
        >
          Refresh
        </button>
        <button
          onClick={() => navigate(compareHref)}
          disabled={compareDisabled}
          title={compareDisabled ? "Select 2-15 runs (checkboxes) to compare" : "Compare selected runs"}
          style={{
            background: compareDisabled ? "#334155" : "#7c3aed",
            color: "#fff", border: "1px solid #5b21b6",
            padding: "8px 16px", borderRadius: 4, fontSize: 13, fontWeight: 600,
            cursor: compareDisabled ? "not-allowed" : "pointer",
          }}
        >
          Compare ({selected.length})
        </button>
        <button
          onClick={() => setSelected([])}
          disabled={selected.length === 0}
          style={{
            background: "#1e293b", color: "#cbd5e1", border: "1px solid #334155",
            padding: "8px 12px", borderRadius: 4, fontSize: 12, cursor: "pointer",
          }}
        >
          Clear
        </button>
        <span style={{ color: "#94a3b8", fontSize: 12 }}>
          {isLoading ? "loading…" : data ? `${data.runs.length} runs` : ""}
        </span>
        {data?.representative_spot_rate_usd_per_hour && (
          <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: "auto" }}>
            Spot rate: {fmtUsd(data.representative_spot_rate_usd_per_hour, 4)}/hr
          </span>
        )}
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>Error: {String(error)}</div>}

      <div className="card" style={{ overflow: "hidden" }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }} title="Select to compare"></th>
                <th>Run</th>
                <th title="Named config profile selected when this run was started. '—' means the default search space was used.">Config</th>
                <th>Status</th>
                <th>Started</th>
                <th className="num">Duration</th>
                <th>Tickers</th>
                <th className="num">Batches done</th>
                <th className="num">Failed</th>
                <th className="num">Aborted</th>
                <th className="num">Spot kills</th>
                <th className="num">Instance hrs</th>
                <th className="num">Total cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.runs ?? []).map((r: TpeHistoryRun) => {
                // Derive a stable run_id from the execution name. Fresh
                // execs are `tpe-<run_id>`; phase1-only are
                // `tpe-p1only-<run_id>`; resumes are
                // `tpe-resume-<run_id>-<HHMMSS>`; targeted phase-2 are
                // `tpe-tgt-<ticker>-<run_id>-<HHMMSS>`. The stored run_id
                // in DDB is always the bare YYYYMMDD_HHMMSS_mmm timestamp.
                const name = r.name ?? "";
                const RUN_ID_PATTERN = /\d{8}_\d{6}_\d{3}/;
                let runId: string;
                if (name.startsWith("tpe-tgt-")) {
                  const m = name.match(RUN_ID_PATTERN);
                  runId = m ? m[0] : name.replace(/^tpe-tgt-/, "");
                } else if (name.startsWith("tpe-resume-")) {
                  runId = name.slice("tpe-resume-".length).replace(/-\d{6}$/, "");
                } else if (name.startsWith("tpe-p1only-")) {
                  runId = name.slice("tpe-p1only-".length);
                } else {
                  runId = name.replace(/^tpe-/, "");
                }
                const isSelected = selected.includes(runId);
                return (
                  <tr key={r.execution_arn} style={isSelected ? { background: "#1e1b4b" } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(runId)}
                        title={isSelected ? "Deselect" : "Select for compare"}
                      />
                    </td>
                    <td><code style={{ fontSize: 11 }}>{name || "—"}</code></td>
                    <td>
                      {r.feature_config_label ? (
                        <span style={{
                          background: "#1e1b4b", color: "#c4b5fd",
                          border: "1px solid #4c1d95",
                          padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                        }}><code style={{ color: "#e0e7ff" }}>{r.feature_config_label}</code></span>
                      ) : <span style={{ color: "#64748b", fontSize: 11 }}>—</span>}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                    <td style={{ fontSize: 12 }}>{fmtDateTime(r.start_date)}</td>
                    <td className="num">{fmtDurationSec(r.duration_seconds)}</td>
                    <td><TickerList tickers={r.tickers} isSubset={r.is_subset} /></td>
                    <td className="num" style={{ color: "#22c55e" }}>{r.batches_succeeded}</td>
                    <td className="num" style={{ color: r.batches_failed > 0 ? "#ef4444" : "#cbd5e1" }}>{r.batches_failed}</td>
                    <td className="num" style={{ color: r.batches_aborted > 0 ? "#c4b5fd" : "#cbd5e1" }}>{r.batches_aborted}</td>
                    <td className="num" style={{ color: r.spot_terminations > 0 ? "#fbbf24" : "#cbd5e1" }}>{r.spot_terminations}</td>
                    <td className="num">{r.instance_hours.toFixed(2)}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{fmtUsd(r.total_cost_usd, 2)}</td>
                    <td>
                      <Link to={`/runs/${encodeURIComponent(runId)}`} style={{ color: "#93c5fd", fontSize: 12 }}>
                        Inspect →
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {data?.runs.length === 0 && (
                <tr><td colSpan={13} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: 20 }}>No TPE runs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ color: "#64748b", fontSize: 11, marginTop: 8, fontStyle: "italic" }}>
        Each "batch" is a Distributed Map item (~jobs_per_batch jobs each — default 10), so total jobs ≈ batches × 10.
      </div>
    </div>
  );
}
